import { type RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { Avatar, Flex, Input, Loader } from "@bdo/cornerstone-ui";
import { RiSearchLine, RiCloseLine } from "@remixicon/react";
import { useDebounceValue } from "@/hooks";
import type { ValidUser } from "@/app/workflows/workflowInstancesApi";

interface UserSearchFormProps {
    filteredResults: ValidUser[];
    isSearching: boolean;
    isDisabled: boolean;
    addError: string | null;
    resetRef: RefObject<(() => void) | null>;
    onSearch: (query: string) => void;
    onSelectUser: (user: ValidUser) => void;
    onClearError: () => void;
}

export function UserSearchForm({
    filteredResults,
    isSearching,
    isDisabled,
    addError,
    resetRef,
    onSearch,
    onSelectUser,
    onClearError,
}: Readonly<UserSearchFormProps>) {
    const { t } = useTranslation();
    const searchDropdownRef = useRef<HTMLFormElement>(null);
    const [showSearchResults, setShowSearchResults] = useState(false);

    const { control, watch, reset, setValue, formState: { errors } } = useForm({
        defaultValues: { searchQuery: "" },
        mode: "onChange",
    });

    const searchQuery = watch("searchQuery");
    const debouncedSearchQuery = useDebounceValue(searchQuery, 300);

    useEffect(() => {
        if (debouncedSearchQuery.trim()) {
            onSearch(debouncedSearchQuery);
            setShowSearchResults(true);
        } else {
            onSearch("");
            setShowSearchResults(false);
        }
    }, [debouncedSearchQuery, onSearch]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                searchDropdownRef.current &&
                !searchDropdownRef.current.contains(event.target as Node)
            ) {
                setShowSearchResults(false);
            }
        }

        if (showSearchResults) {
            document.addEventListener("mousedown", handleClickOutside, true);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside, true);
            };
        }
    }, [showSearchResults]);

    // Expose reset to parent via ref
    useEffect(() => {
        resetRef.current = () => {
            reset();
            setShowSearchResults(false);
        };
    }, [reset, resetRef]);

    return (
        <form
            onSubmit={(e) => e.preventDefault()}
            className="relative"
            ref={searchDropdownRef}
            data-testid="user-search-form"
        >
            <Controller
                name="searchQuery"
                control={control}
                rules={{
                    minLength: {
                        value: 1,
                        message: t("manageAccess.searchInput.minLengthError", {
                            defaultValue: "Please enter at least 1 character to search.",
                        }),
                    },
                }}
                render={({ field }) => (
                    <Input
                        label={t("manageAccess.searchInput.label", {
                            defaultValue: "Add people to workflow",
                        })}
                        hideLabel
                        placeholder={t("manageAccess.searchInput.placeholder", {
                            defaultValue: "Add people to workflow",
                        })}
                        prefix={<RiSearchLine />}
                        hideSuffixBorder
                        suffix={
                            field.value && (
                                <RiCloseLine
                                    onClick={() => {
                                        setValue("searchQuery", "");
                                        setShowSearchResults(false);
                                        onClearError();
                                    }}
                                    className="cursor-pointer"
                                    aria-label="Clear search input"
                                    data-testid="user-search-clear-button"
                                />
                            )
                        }
                        value={field.value}
                        onChange={(e) => {
                            field.onChange(e);
                            onClearError();
                        }}
                        errorMessage={errors.searchQuery?.message || addError || undefined}
                        disabled={isDisabled}
                    />
                )}
            />


            {showSearchResults && (isSearching || filteredResults.length > 0) && (
                <div className="absolute top-full left-0 right-0 py-sm bg-white outline outline-1 -outline-offset-1 outline-shade-lighter shadow-bdo-menu z-20 max-h-[240px] overflow-y-auto overflow-x-hidden flex flex-col gap-xs" data-testid="user-search-results-dropdown">
                    {isSearching && (
                        <div className="flex justify-center py-sm" data-testid="user-search-loader">
                            <Loader size={24} />
                        </div>
                    )}
                    {!isSearching && filteredResults.map((user) => (
                        <button
                            type="button"
                            key={user.email}
                            data-testid={`user-search-result-${user.email}`}
                            onClick={() => {
                                onSelectUser(user);
                            }}
                            className="w-full px-sm py-2xs hover:bg-shade-lightest text-left flex items-center gap-xs transition-colors cursor-pointer"
                        >
                            <Avatar size="sm" name={user.displayName || user.email} isCircle />
                            <Flex direction="column" className="flex-1 min-w-0">
                                <div className="text-base text-mono-darker truncate" title={user.displayName}>{user.displayName}</div>
                                <div className="text-sm text-mono truncate" title={user.email}>{user.email}</div>
                            </Flex>
                        </button>
                    ))}
                </div>
            )}
        </form>
    );
}
